/**
 * useAggregatr Hook
 * 
 * React hook for accessing Aggregatr instance in React components
 * 
 * CHANGE HISTORY:
 * - 251102_1100 [pending] danielbeer: Create useAggregatr hook for Delivvr app
 */

import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { Aggregatr } from '../Aggregatr';

// Create context
const AggregatrContext = createContext<Aggregatr | null>(null);

// Provider props
interface AggregatrProviderProps {
  children: ReactNode;
  aggregatr?: Aggregatr;
}

/**
 * AggregatrProvider
 * 
 * Provides Aggregatr instance to React component tree
 */
export function AggregatrProvider({ children, aggregatr }: AggregatrProviderProps) {
  // Create instance once (stable across renders)
  const [instance] = useState(() => aggregatr || new Aggregatr());
  
  // Memoize value to prevent infinite re-renders
  const value = useMemo(() => instance, [instance]);
  
  return (
    <AggregatrContext.Provider value={value}>
      {children}
    </AggregatrContext.Provider>
  );
}

/**
 * useAggregatr Hook
 * 
 * Access Aggregatr instance from React components
 * 
 * @returns Aggregatr instance
 * @throws Error if used outside AggregatrProvider
 * 
 * @example
 * ```typescript
 * function MyComponent() {
 *   const aggregatr = useAggregatr();
 *   
 *   const loadData = async () => {
 *     const data = await aggregatr.read('users', { active: true });
 *     console.log(data);
 *   };
 *   
 *   return <button onClick={loadData}>Load Users</button>;
 * }
 * ```
 */
export function useAggregatr(): Aggregatr {
  const context = useContext(AggregatrContext);
  
  if (!context) {
    throw new Error('useAggregatr must be used within AggregatrProvider');
  }
  
  return context;
}
